/**
 * Cliente fetch tipado da API do trt1-prazos.
 *
 * Em DEV, Vite lê VITE_API_BASE do .env / env do shell.
 * Em produção (build estático no nginx do VPS), API roda no mesmo host
 * (porta 8080 ou via proxy reverso) — defina VITE_API_BASE em build time.
 *
 * Header X-Mock-User é injetado automaticamente a partir do localStorage
 * (preenchido pela página de login).
 */

import type {
  AgendarAudienciaIn,
  AlertaReconc,
  ArquivoDrive,
  AtualizarUsuarioIn,
  AudienciaItem,
  ColunaCards,
  ConferirResponse,
  ConfigFinanceiro,
  ContagemEtiqueta,
  CustosConfig,
  CustosPainel,
  UsoIaEvento,
  CriarAudienciaIn,
  CriarCasoIn,
  CriarPrazoIn,
  DadosPeticao,
  DashboardFinanceiro,
  EditarPrazoIn,
  EscritorioRegistro,
  FinReceitaFiltros,
  GerarPeticaoResponse,
  LoginEvento,
  ModeloPecaStatus,
  ProcessoPrefill,
  Receita,
  ReceitaIn,
  ReceitaUpdateIn,
  Socio,
  SocioIn,
  CriarUsuarioIn,
  DatajudInfo,
  DriveStatus,
  EndpointTransicao,
  EventoAudit,
  HealthOut,
  KanbanSemana,
  PecasStatus,
  PrazoCard,
  PrazoDetalhe,
  ProcessoBusca,
  RefinementRequest,
  RefinementResponse,
  SessaoUsuario,
  StagingStatusOut,
  StagingUploadOut,
  TransicaoResponse,
  TransicoesPermitidas,
  UploadResponse,
  UsuarioOut,
} from './types';
import { auth } from './auth';

/**
 * Base URL da API:
 *  - PROD (build do nginx)   → VITE_API_BASE='' → same-origin (fetch '/api/...')
 *    nginx faz proxy_pass de /api/ /auth/ /healthz pro 127.0.0.1:8080.
 *  - DEV local (npm run dev) → fallback para o IP:porta direto do VPS.
 *  - Override manual: localStorage.apiBase (útil em testes).
 */
const DEFAULT_BASE =
  import.meta.env.VITE_API_BASE !== undefined
    ? import.meta.env.VITE_API_BASE
    // PROD (build): same-origin ('') — funciona em http://IP e https://domínio
    // (sem mixed content). DEV: localhost:8080 (vite dev em :5173, API noutra porta).
    : (import.meta.env.DEV ? 'http://localhost:8080' : '');

export const apiBase = (): string => {
  const override = localStorage.getItem('apiBase');
  if (override !== null) return override;
  return DEFAULT_BASE;
};

/**
 * Monta a URL WebSocket a partir do apiBase:
 *  - PROD (apiBase='') → same-origin: ws(s)://<host atual><path>
 *  - DEV (apiBase=http://IP:porta) → ws://IP:porta<path>
 */
export const wsUrl = (path: string): string => {
  const base = apiBase();
  if (!base) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}${path}`;
  }
  return base.replace(/^http/i, 'ws') + path;
};

export const setApiBase = (b: string | null) => {
  if (b === null || b === DEFAULT_BASE) {
    localStorage.removeItem('apiBase');
  } else {
    localStorage.setItem('apiBase', b);
  }
};

// ── Impersonação de escritório (super usuário) ──────────────────────────────
// Quando setado, TODAS as chamadas mandam X-Escritorio=<id> e o backend (super)
// troca o tenant da request. '' / ausente = o próprio escritório (sem override).
const XESC_KEY = 'xEscritorio';
export const getXEscritorio = (): string | null =>
  typeof localStorage !== 'undefined' ? localStorage.getItem(XESC_KEY) : null;
export const setXEscritorio = (id: string | null) => {
  if (typeof localStorage === 'undefined') return;
  if (!id) localStorage.removeItem(XESC_KEY);
  else localStorage.setItem(XESC_KEY, id);
};

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API ${status}: ${body.slice(0, 200)}`);
  }
}

async function _fetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  // Impersonação de escritório (super usuário): aplica o tenant ativo, salvo se
  // a chamada já trouxe o header explicitamente (ex.: painel de Escritórios).
  const xEsc = getXEscritorio();
  if (xEsc && !('X-Escritorio' in headers)) headers['X-Escritorio'] = xEsc;

  const r = await fetch(`${apiBase()}${path}`, {
    ...init,
    headers,
    credentials: 'same-origin', // envia o cookie JWT (sessão por senha) same-origin
    signal: init.signal ?? AbortSignal.timeout(10_000),
  });
  if (!r.ok) {
    // Sessão expirada/inválida → derruba o login local e volta à tela de login.
    // Endpoints PRÉ-auth (login/signup/oauth): um 401 ali = credencial errada,
    // mostrado inline na tela. Os demais — inclusive /auth/trocar-senha e
    // /auth/logout — são AUTENTICADOS: 401 = sessão perdida (cookie ausente/
    // expirado, ou sessão única derrubou) → derruba o login local e manda pro
    // /login com aviso, em vez de deixar o usuário num beco com JSON cru.
    const preAuth = path.startsWith('/auth/login') || path.startsWith('/auth/signup')
      || path.startsWith('/auth/callback');
    if (r.status === 401 && !preAuth && auth.isLogged()) {
      auth.logout();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login?motivo=sessao_encerrada');
      }
    }
    const body = await r.text().catch(() => '');
    throw new ApiError(r.status, body || r.statusText);
  }
  // 204 / vazio
  const txt = await r.text();
  return (txt ? JSON.parse(txt) : null) as T;
}

// ── Jurisprudência (base local juris.db + inclusão manual) ──
export interface JurisMeta {
  tribunal: string; grau: string; classe: string; numero_processo: string;
  relator: string; orgao_julgador: string; data_julgamento: string;
  ementa: string; favorabilidade: string; favorabilidade_motivo?: string;
  escopo: 'publico' | 'cliente' | 'segredo';
}
export interface JurisStats {
  total: number; com_turma: number;
  por_fonte: Record<string, number>; por_tribunal: Record<string, number>; por_escopo: Record<string, number>;
}
export interface JurisResultado {
  id: number; fonte: string; tribunal: string; turma: string | null; relator: string | null;
  numero_processo: string; data_julgamento: string; favorabilidade: string | null; ementa: string; url: string | null;
}
/** Resultado normalizado de /api/falcao/search (jurisprudência oficial JT). */
export interface FalcaoItem {
  id: string; tribunal: string; numero_processo: string; numero: string;
  sigla_classe?: string; classe_processo?: string; relator?: string; orgao_julgador?: string;
  data_julgamento?: string; data?: string; ementa: string; snippet?: string;
  inteiro_teor?: string; possui_ementa?: boolean; url?: string; url_inteiro_teor?: string;
}

export const api = {
  health: () => _fetch<HealthOut>('/healthz'),
  me: () => _fetch<UsuarioOut>('/api/me'),
  // Segurança — trilha de logins do escritório (admin/gerente)
  listarLogins: (anomalias = false, limite = 100) =>
    _fetch<LoginEvento[]>(`/api/seguranca/logins?limite=${limite}&anomalias=${anomalias}`),
  // Reconciliação (gap-detector) — alertas de possível prazo não-capturado
  reconciliacaoResumo: () =>
    _fetch<{ abertos: number; processos: number; por_movimento: Record<string, number> }>(
      '/api/reconciliacao/resumo'),
  reconciliacaoAlertas: (status = 'aberto') =>
    _fetch<AlertaReconc[]>(`/api/reconciliacao/alertas?status=${encodeURIComponent(status)}`),
  reconciliacaoResolver: (chave: string, status: string) =>
    _fetch<unknown>('/api/reconciliacao/alertas/resolver',
      { method: 'POST', body: JSON.stringify({ chave, status }) }),
  // Modelos de petição (aba Peticionar com modelo)
  listarModelosPeca: () =>
    _fetch<Record<string, ModeloPecaStatus>>('/api/modelos-peca'),
  uploadModeloPeca: async (key: string, file: File): Promise<{ ok: boolean; drive_file_id: string; nome: string }> => {
    const fd = new FormData();
    fd.append('arquivo', file);
    const xEsc = getXEscritorio();
    const headers: Record<string, string> = {};
    if (xEsc) headers['X-Escritorio'] = xEsc;
    const r = await fetch(`${apiBase()}/api/modelos-peca/${encodeURIComponent(key)}`, {
      method: 'POST', credentials: 'same-origin', headers, body: fd,
    });
    if (!r.ok) throw new ApiError(r.status, (await r.text().catch(() => '')) || r.statusText);
    return r.json();
  },
  excluirModeloPeca: (key: string) =>
    _fetch<{ ok: boolean; removido: boolean }>(`/api/modelos-peca/${encodeURIComponent(key)}`,
      { method: 'DELETE' }),
  // Peticionar com modelo — metade direita (dados do processo + conferência)
  dadosPeticao: (idPrazo: number) =>
    _fetch<DadosPeticao>(`/api/peticionar/${idPrazo}/dados`),
  conferirPeticao: (idPrazo: number, pieceKey: string) =>
    _fetch<ConferirResponse>(`/api/peticionar/${idPrazo}/conferir`,
      { method: 'POST', body: JSON.stringify({ piece_key: pieceKey }) }),
  gerarPeticaoModelo: (idPrazo: number, pieceKey: string, overrides: Record<string, string>) =>
    _fetch<GerarPeticaoResponse>(`/api/peticionar/${idPrazo}/gerar`,
      { method: 'POST', body: JSON.stringify({ piece_key: pieceKey, overrides }) }),
  // ── Jurisprudência (base local + inclusão manual) — admin/gerente ─────────
  jurisStats: () => _fetch<JurisStats>('/api/jurisprudencia/stats'),
  jurisBuscar: (q: string, tribunal?: string, turma?: string) => {
    const p = new URLSearchParams({ q });
    if (tribunal) p.set('tribunal', tribunal);
    if (turma) p.set('turma', turma);
    return _fetch<{ resultados: JurisResultado[]; total: number }>(`/api/jurisprudencia/buscar?${p.toString()}`);
  },
  jurisExtrair: async (file: File | null, texto: string): Promise<{ metadados: JurisMeta; texto: string; chars: number }> => {
    const fd = new FormData();
    if (file) fd.append('arquivo', file);
    if (texto) fd.append('texto', texto);
    const xEsc = getXEscritorio();
    const headers: Record<string, string> = {};
    if (xEsc) headers['X-Escritorio'] = xEsc;
    const r = await fetch(`${apiBase()}/api/jurisprudencia/extrair`, {
      method: 'POST', credentials: 'same-origin', headers, body: fd,
      signal: AbortSignal.timeout(180_000),  // auto-metadados via Haiku pode levar ~1min
    });
    if (!r.ok) throw new ApiError(r.status, (await r.text().catch(() => '')) || r.statusText);
    return r.json();
  },
  jurisIncluir: (meta: JurisMeta, texto: string) =>
    _fetch<{ id: number; escopo: string; nota_vault: string | null; citavel: boolean; numero_processo: string; tribunal: string }>(
      '/api/jurisprudencia/incluir',
      { method: 'POST', body: JSON.stringify({ meta, texto }), signal: AbortSignal.timeout(60_000) }),
  // Consulta ao Falcão (oficial JT) — mesma dep de login; tenant via X-Escritorio
  falcaoTribunais: () =>
    _fetch<{ tribunais: { sigla: string; nome: string }[] }>('/api/falcao/tribunais'),
  falcaoSearch: (q: string, tribunal?: string) => {
    const p = new URLSearchParams({ q, limit: '10' });
    if (tribunal) p.set('tribunais', tribunal);
    return _fetch<{ resultados: FalcaoItem[]; total: number }>(
      `/api/falcao/search?${p.toString()}`, { signal: AbortSignal.timeout(60_000) });
  },
  // Curadoria via Falcão: inclui na base do tenant as ementas selecionadas na busca
  jurisFalcaoIncluir: (itens: unknown[]) =>
    _fetch<{ incluidos: { id: number; numero_processo: string; tribunal: string }[]; total: number; erros: string[] }>(
      '/api/jurisprudencia/incluir-falcao',
      { method: 'POST', body: JSON.stringify({ itens }), signal: AbortSignal.timeout(120_000) }),
  // ── Financeiro (admin) ──────────────────────────────────────────────────
  obterConfigFinanceiro: () => _fetch<ConfigFinanceiro>('/api/financeiro/config'),
  salvarConfigFinanceiro: (pct_contratuais_padrao: number) =>
    _fetch<ConfigFinanceiro>('/api/financeiro/config',
      { method: 'PUT', body: JSON.stringify({ pct_contratuais_padrao }) }),
  listarSocios: (incluirInativos = false) =>
    _fetch<Socio[]>(`/api/financeiro/socios${incluirInativos ? '?incluir_inativos=true' : ''}`),
  criarSocio: (body: SocioIn) =>
    _fetch<Socio>('/api/financeiro/socios', { method: 'POST', body: JSON.stringify(body) }),
  atualizarSocio: (id: number, body: Partial<SocioIn>) =>
    _fetch<Socio>(`/api/financeiro/socios/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  excluirSocio: (id: number) =>
    _fetch<{ ok: boolean; removido: boolean }>(`/api/financeiro/socios/${id}`, { method: 'DELETE' }),
  listarReceitas: (f: FinReceitaFiltros = {}) => {
    const p = new URLSearchParams();
    if (f.status) p.set('status', f.status);
    if (f.numero_processo) p.set('numero_processo', f.numero_processo);
    if (f.data_ini) p.set('data_ini', f.data_ini);
    if (f.data_fim) p.set('data_fim', f.data_fim);
    const qs = p.toString();
    return _fetch<Receita[]>(`/api/financeiro/receitas${qs ? `?${qs}` : ''}`);
  },
  obterReceita: (id: number) => _fetch<Receita>(`/api/financeiro/receitas/${id}`),
  criarReceita: (body: ReceitaIn) =>
    _fetch<Receita>('/api/financeiro/receitas', { method: 'POST', body: JSON.stringify(body) }),
  atualizarReceita: (id: number, body: ReceitaUpdateIn) =>
    _fetch<Receita>(`/api/financeiro/receitas/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  marcarReceitaRecebida: (id: number, data_recebimento?: string, valor_recebido?: number) =>
    _fetch<Receita>(`/api/financeiro/receitas/${id}/receber`,
      { method: 'POST', body: JSON.stringify({ data_recebimento, valor_recebido }) }),
  excluirReceita: (id: number) =>
    _fetch<{ ok: boolean; removido: boolean }>(`/api/financeiro/receitas/${id}`, { method: 'DELETE' }),
  dashboardFinanceiro: () => _fetch<DashboardFinanceiro>('/api/financeiro/dashboard'),
  // ── Custos (monitor de gasto da assinatura) ──────────────────────────────
  custosPainel: (escopo: 'tenant' | 'global' = 'tenant') =>
    _fetch<CustosPainel>(`/api/custos/painel?escopo=${escopo}`),
  custosEventos: (escopo: 'tenant' | 'global' = 'tenant', limit = 40) =>
    _fetch<{ eventos: UsoIaEvento[] }>(`/api/custos/eventos?escopo=${escopo}&limit=${limit}`),
  custosConfig: () => _fetch<CustosConfig>('/api/custos/config'),
  salvarCustosConfig: (body: { assinatura_custo_mensal_reais?: number; assinatura_tokens_dia?: number; assinatura_tokens_semana?: number }) =>
    _fetch<{ ok: boolean; config: CustosConfig }>('/api/custos/config',
      { method: 'POST', body: JSON.stringify(body) }),
  prefillProcesso: (numero: string) =>
    _fetch<ProcessoPrefill>(`/api/financeiro/processo/${encodeURIComponent(numero)}`),
  avocar: (id: number) =>
    _fetch<unknown>(`/api/prazos/${id}/avocar`, { method: 'POST' }),
  branding: () =>
    _fetch<{ escritorio_id: number; nome_exibicao: string; ramo: string;
             cor_primaria: string; tem_logo: boolean }>('/api/branding'),
  // ── Auth por senha ──────────────────────────────────────────────────────
  authConfig: () => _fetch<{ oauth_enabled: boolean }>('/auth/config'),
  loginSenha: (usuario: string, senha: string) =>
    _fetch<SessaoUsuario & { ok: boolean }>('/auth/login-senha', {
      method: 'POST',
      body: JSON.stringify({ usuario, senha }),
    }),
  // Cadastro freemium (SaaS v4) — cria escritório + usuário fundador + auto-login.
  signup: (dados: {
    nome: string; email: string; senha: string;
    escritorio_nome: string; oab_numero: string; oab_uf: string;
  }) =>
    _fetch<{ ok: boolean; escritorio_id: number; plano: string; nome: string }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(dados),
    }),
  trocarSenha: (senha_atual: string, senha_nova: string) =>
    _fetch<{ ok: boolean }>('/auth/trocar-senha', {
      method: 'POST',
      body: JSON.stringify({ senha_atual, senha_nova }),
    }),
  usuarios: (incluirInativos = false) =>
    _fetch<UsuarioOut[]>(
      incluirInativos ? '/api/usuarios?incluir_inativos=true' : '/api/usuarios',
    ),
  prazosSemana: (semanaIso?: string) =>
    _fetch<KanbanSemana>(
      semanaIso ? `/api/prazos?semana=${semanaIso}` : '/api/prazos',
    ),
  // Super usuário: lista os escritórios (para o seletor de impersonação)
  listarEscritorios: () => _fetch<EscritorioRegistro[]>('/api/admin/escritorios'),
  prazoDetalhe: (id: number) => _fetch<PrazoDetalhe>(`/api/prazos/${id}`),
  buscarPrazos: (numero: string) =>
    _fetch<PrazoCard[]>(`/api/prazos/buscar?numero=${encodeURIComponent(numero)}`),
  // DataJud (CNJ) — metadados + timeline de movimentos do processo.
  datajud: (id: number) =>
    _fetch<DatajudInfo>(`/api/prazos/${id}/datajud`, { signal: AbortSignal.timeout(30_000) }),
  historico: (id: number) =>
    _fetch<EventoAudit[]>(`/api/prazos/${id}/historico`),
  kanbanStatus: () => _fetch<ContagemEtiqueta>('/api/kanban/status'),
  transicoesPermitidas: (id: number) =>
    _fetch<TransicoesPermitidas>(`/api/prazos/${id}/transicoes-permitidas`),

  // Transições (POST). `nome` é o segmento do path.
  transicao: (id: number, nome: EndpointTransicao, observacao?: string) =>
    _fetch<TransicaoResponse>(`/api/prazos/${id}/${nome}`, {
      method: 'POST',
      body: observacao ? JSON.stringify({ observacao }) : '{}',
    }),

  // ── Overhaul v4: criar/excluir card manual (TRIAGEM) ──────────────────────
  criarPrazo: (body: CriarPrazoIn) =>
    _fetch<PrazoCard>('/api/prazos', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // ── Staging: upload de autos ANTES de existir o prazo/card ────────────────
  // XHR (não fetch) para expor progresso de UPLOAD — autos podem ter centenas de
  // MB; sem isso o PDF "some" por vários segundos sem feedback. onProgress = 0-100.
  uploadStaging: (
    arquivo: File,
    onProgress?: (pct: number) => void,
  ): Promise<StagingUploadOut> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const form = new FormData();
      form.append('arquivo', arquivo);
      xhr.open('POST', `${apiBase()}/api/staging/upload`);
      xhr.withCredentials = true;
      const xEsc = getXEscritorio();
      if (xEsc) xhr.setRequestHeader('X-Escritorio', xEsc);
      const mockUser = localStorage.getItem('mockUser');
      if (mockUser) xhr.setRequestHeader('X-Mock-User', mockUser);
      xhr.timeout = 900_000; // 15 min — autos até 500 MB
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new ApiError(0, 'resposta inválida do upload')); }
        } else {
          reject(new ApiError(xhr.status, xhr.responseText || xhr.statusText));
        }
      };
      xhr.onerror = () => reject(new ApiError(0, 'erro de rede no upload'));
      xhr.ontimeout = () => reject(new ApiError(0, 'tempo esgotado no upload'));
      xhr.send(form);
    }),
  stagingStatus: (token: string) =>
    _fetch<StagingStatusOut>(`/api/staging/${token}/status`),
  // ── Cards por processo (modelo perícias: buscar antes de criar) ───────────
  buscarProcessos: (numero: string) =>
    _fetch<ProcessoBusca[]>(`/api/processos/buscar?numero=${encodeURIComponent(numero)}`),
  servicosProcesso: (processoId: number) =>
    _fetch<PrazoCard[]>(`/api/processos/${processoId}/servicos`),
  // Substitui o PDF dos autos do processo (autos atualizados) — XHR c/ progresso,
  // mesmo molde do uploadStaging (autos podem ter centenas de MB).
  substituirPdfProcesso: (
    processoId: number,
    arquivo: File,
    onProgress?: (pct: number) => void,
  ): Promise<{ ok: boolean; pdf_nome: string | null; pdf_atualizado_em: string | null }> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const form = new FormData();
      form.append('arquivo', arquivo);
      xhr.open('POST', `${apiBase()}/api/processos/${processoId}/substituir-pdf`);
      xhr.withCredentials = true;
      const xEsc = getXEscritorio();
      if (xEsc) xhr.setRequestHeader('X-Escritorio', xEsc);
      const mockUser = localStorage.getItem('mockUser');
      if (mockUser) xhr.setRequestHeader('X-Mock-User', mockUser);
      xhr.timeout = 900_000; // 15 min — autos até 500 MB
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new ApiError(0, 'resposta inválida do upload')); }
        } else {
          reject(new ApiError(xhr.status, xhr.responseText || xhr.statusText));
        }
      };
      xhr.onerror = () => reject(new ApiError(0, 'erro de rede no upload'));
      xhr.ontimeout = () => reject(new ApiError(0, 'tempo esgotado no upload'));
      xhr.send(form);
    }),
  editarPrazo: (id: number, body: EditarPrazoIn) =>
    _fetch<PrazoDetalhe>(`/api/prazos/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // ── Casos (aba Petições Iniciais) ─────────────────────────────────────────
  casosKanban: () => _fetch<ColunaCards[]>('/api/casos/kanban'),
  criarCaso: (body: CriarCasoIn) =>
    _fetch<PrazoCard>('/api/casos', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Sobe UM documento do cliente para o caso (petição inicial). Grava SEMPRE na
   * pasta local do tenant (uploads/{id}/) — qualquer extensão — que é de onde a
   * Análise inicial lê. Drive é backup best-effort no backend. */
  uploadDocumentoCaso: (
    idPrazo: number,
    arquivo: File,
    onProgress?: (pct: number) => void,
  ): Promise<{ ok: boolean; nome: string; bytes: number; drive_ok: boolean }> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const form = new FormData();
      form.append('arquivo', arquivo);
      xhr.open('POST', `${apiBase()}/api/casos/${idPrazo}/documentos`);
      xhr.withCredentials = true;
      const xEsc = getXEscritorio();
      if (xEsc) xhr.setRequestHeader('X-Escritorio', xEsc);
      const mockUser = localStorage.getItem('mockUser');
      if (mockUser) xhr.setRequestHeader('X-Mock-User', mockUser);
      xhr.timeout = 600_000;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { resolve({ ok: true, nome: arquivo.name, bytes: arquivo.size, drive_ok: false }); }
        } else {
          reject(new ApiError(xhr.status, xhr.responseText || xhr.statusText));
        }
      };
      xhr.onerror = () => reject(new ApiError(0, 'erro de rede no upload'));
      xhr.ontimeout = () => reject(new ApiError(0, 'tempo esgotado no upload'));
      xhr.send(form);
    }),
  /** Dispara a Análise inicial (IA): lê os documentos do cliente já subidos e
   * gera mapa+blocos com a skill de petição inicial do escritório. */
  analiseInicial: (idPrazo: number) =>
    _fetch<{ ok: boolean; status: string }>(`/api/casos/${idPrazo}/analise-inicial`, {
      method: 'POST',
    }),
  excluirPrazo: (id: number) =>
    _fetch<{ ok: boolean; id_prazo: number; status: string }>(
      `/api/prazos/${id}`,
      { method: 'DELETE' },
    ),

  // ── Overhaul v4: CRUD de usuários (admin) ─────────────────────────────────
  criarUsuario: (body: CriarUsuarioIn) =>
    _fetch<UsuarioOut & { senha_provisoria: string }>('/api/usuarios', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  atualizarUsuario: (id: number, body: AtualizarUsuarioIn) =>
    _fetch<UsuarioOut>(`/api/usuarios/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  resetarSenhaUsuario: (id: number) =>
    _fetch<{ ok: boolean; senha_provisoria: string }>(`/api/usuarios/${id}/resetar-senha`, {
      method: 'POST',
    }),
  excluirUsuario: (id: number) =>
    _fetch<void>(`/api/usuarios/${id}`, {
      method: 'DELETE',
    }),

  // ── Overhaul v4: audiências ───────────────────────────────────────────────
  listarAudiencias: () => _fetch<AudienciaItem[]>('/api/audiencias'),
  agendarAudiencia: (id: number, body: AgendarAudienciaIn) =>
    _fetch<AudienciaItem>(`/api/audiencias/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // Inclusão manual de audiência não cadastrada (cria prazo + card + marca evento)
  criarAudiencia: (body: CriarAudienciaIn) =>
    _fetch<AudienciaItem>('/api/audiencias', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Drive (Fase 5)
  driveStatus: () => _fetch<DriveStatus>('/api/drive/status'),
  listarArquivos: (id: number) =>
    _fetch<ArquivoDrive[]>(`/api/prazos/${id}/arquivos`),
  excluirArquivo: (id: number, fileId: string) =>
    _fetch<{ ok: boolean }>(`/api/prazos/${id}/arquivos/${fileId}`, {
      method: 'DELETE',
    }),

  // Upload multipart com PROGRESSO. fetch() não expõe progresso de upload, então
  // usamos XMLHttpRequest (upload.onprogress). onProgress recebe 0-100.
  uploadPeticao: (
    id: number,
    arquivo: File,
    onProgress?: (pct: number) => void,
  ): Promise<UploadResponse> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const form = new FormData();
      form.append('arquivo', arquivo);
      xhr.open('POST', `${apiBase()}/api/prazos/${id}/upload-peticao`);
      const xEsc = getXEscritorio();
      if (xEsc) xhr.setRequestHeader('X-Escritorio', xEsc);
      const mockUser = localStorage.getItem('mockUser');
      if (mockUser) xhr.setRequestHeader('X-Mock-User', mockUser);
      xhr.timeout = 600_000; // 10 min — PDFs de até 200 MB
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { resolve(null as unknown as UploadResponse); }
        } else {
          reject(new ApiError(xhr.status, xhr.responseText || xhr.statusText));
        }
      };
      xhr.onerror = () => reject(new ApiError(0, 'erro de rede no upload'));
      xhr.ontimeout = () => reject(new ApiError(0, 'tempo esgotado no upload'));
      xhr.send(form);
    }),

  logout: () =>
    _fetch<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  // Peças processuais (Fase 9 / Fase 10)
  pecasStatus: (id: number) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/pecas-status`),
  obterMapaConteudo: (id: number) =>
    _fetch<{ conteudo: string; versao?: number }>(`/api/prazos/${id}/mapa-conteudo`),
  obterBlocosConteudo: (id: number) =>
    _fetch<{ blocos: any[]; versao?: number }>(`/api/prazos/${id}/blocos-conteudo`),
  // Blocos VIVOS do workdir (arquivo que o Claude edita no refino) — live-refresh
  obterBlocosWorkdir: (id: number) =>
    _fetch<{ blocos: any[]; mtime?: number; fonte?: string }>(`/api/prazos/${id}/blocos-workdir`),
  // Teses propostas pela última "Gerar Petição" (pendentes de aprovação p/ o arsenal)
  obterTesesPropostas: (id: number) =>
    _fetch<{ teses: any[] }>(`/api/prazos/${id}/teses-propostas`),
  // Mescla as teses aprovadas/editadas no arsenal.md (add_tese.py --commit --push)
  atualizarArsenal: (id: number, teses: any[]) =>
    _fetch<{ adicionadas: number; total: number; push_ok: boolean; resultados: any[] }>(
      `/api/prazos/${id}/atualizar-arsenal`,
      { method: 'POST', body: JSON.stringify({ teses }), signal: AbortSignal.timeout(180_000) },
    ),
  // Verificação física forçada dos arquivos no Drive (limpa IDs obsoletos)
  verificarPecas: (id: number) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/verificar-pecas`, { method: 'POST' }),
  // OCR — torna o PDF pesquisável + extrai texto (sobe .txt ao Drive).
  // Dispatch é fire-and-forget no VPS; timeout generoso só p/ o disparo.
  iniciarOcr: (id: number) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/ocr`, {
      method: 'POST',
      signal: AbortSignal.timeout(120_000),
    }),
  refinarRascunho: (id: number, req: RefinementRequest) =>
    _fetch<RefinementResponse>(`/api/prazos/${id}/refinar-rascunho`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),
  // Fase 12 — pipeline UNIFICADO "Mapa do Processo":
  // Split → OCR → pdftotext → Mapa → Blocos, com barra de progresso por etapa.
  iniciarProcesso: (id: number) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/iniciar-processo`, {
      method: 'POST',
      signal: AbortSignal.timeout(120_000),  // só o disparo; pipeline roda em background
    }),
  // ── Pipeline DOCAS (Stage A → gate → Stage B) ──────────────────────────────
  // Dispara o pipeline completo: preparo (split→OCR→classify→pack) → mapa.
  iniciarPipeline: (id: number, peca?: { tipo_peca: string; peca_rotulo: string; paginas_ignorar?: string }) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/iniciar-pipeline`, {
      method: 'POST',
      ...(peca ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(peca) } : {}),
      signal: AbortSignal.timeout(120_000),  // só o disparo; roda em background
    }),
  // Re-gera o .docx da ÚLTIMA versão dos blocos (determinístico, sem Claude) —
  // botão "Re-gerar .docx" na Revisão após refinar ali.
  renderizarPeca: (id: number) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/render-ultima`, {
      method: 'POST',
      signal: AbortSignal.timeout(120_000),
    }),
  // Move a etapa MACRO do card (ex.: Refinamento "Gerar a peça" → 'redigindo').
  setEtapa: (id: number, etapa: string, msg = '') =>
    _fetch<PecasStatus>(`/api/prazos/${id}/etapa-pipeline`, {
      method: 'POST',
      body: JSON.stringify({ etapa, msg }),
    }),
  // Gate humano: aprova o mapa e dispara a Stage B (blocos → render → anti-IA).
  aprovarMapa: (id: number) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/aprovar-mapa`, {
      method: 'POST',
      signal: AbortSignal.timeout(120_000),
    }),
  // Fase 11 — geração independente por artefato (legado: ainda usado pelo "Tentar novamente")
  iniciarMapa: (id: number) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/iniciar-mapa`, { method: 'POST' }),
  iniciarBlocos: (id: number) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/iniciar-blocos`, { method: 'POST' }),
  // Legado: gera mapa+blocos juntos (usado pelo botão "Tentar novamente" em erro genérico)
  iniciarGeracao: (id: number) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/iniciar-geracao`, { method: 'POST' }),
  // Spawna gerar-peticao.sh no VPS.
  // tipoPeca opcional: se omitido o backend auto-detecta pelo tipo_prazo do card.
  // Render SÍNCRONO da petição final a partir da última versão dos blocos.
  gerarPeticao: (id: number, tipoPeca?: string) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/gerar-peticao`, {
      method: 'POST',
      body: JSON.stringify(tipoPeca ? { tipo_peca: tipoPeca } : {}),
      signal: AbortSignal.timeout(120_000),
    }),
  // ── Refino conversacional (terminal amigável = chat) ───────────────────────
  // Prepara o workdir com a última versão dos blocos + zera a sessão do chat.
  refinarIniciar: (id: number) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/refinar-iniciar`, {
      method: 'POST',
      signal: AbortSignal.timeout(120_000),
    }),
  // 1 turno do chat: o Claude edita os blocos no workdir e responde. Síncrono.
  refinarChat: (id: number, mensagem: string) =>
    _fetch<{ resposta: string }>(`/api/prazos/${id}/refinar-chat`, {
      method: 'POST',
      body: JSON.stringify({ mensagem }),
      signal: AbortSignal.timeout(320_000),  // claude --print pode levar minutos
    }),
  // STREAMING (SSE): exibe o echo das mensagens da LLM conforme são produzidas.
  refinarChatStream: async (
    id: number,
    mensagem: string,
    cb: { onDelta: (t: string) => void; onDone: (full: string) => void; onErro: (e: string) => void },
  ): Promise<void> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const mock = localStorage.getItem('mockUser');
    if (mock) headers['X-Mock-User'] = mock;
    let resp: Response;
    try {
      resp = await fetch(`${apiBase()}/api/prazos/${id}/refinar-chat-stream`, {
        method: 'POST', headers, credentials: 'same-origin',
        body: JSON.stringify({ mensagem }),
      });
    } catch (e: any) {
      cb.onErro(e?.message ?? 'erro de rede'); return;
    }
    if (!resp.ok || !resp.body) {
      cb.onErro(`HTTP ${resp.status}`); return;
    }
    const reader = resp.body.getReader();
    const dec = new TextDecoder();
    let buf = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const partes = buf.split('\n\n');
      buf = partes.pop() ?? '';
      for (const p of partes) {
        const linha = p.trim();
        if (!linha.startsWith('data:')) continue;
        const json = linha.slice(5).trim();
        if (!json) continue;
        try {
          const ev = JSON.parse(json);
          if (typeof ev.delta === 'string') cb.onDelta(ev.delta);
          else if (typeof ev.done === 'string') cb.onDone(ev.done);
          else if (ev.erro) cb.onErro(ev.erro);
        } catch { /* linha parcial — ignora */ }
      }
    }
  },
  // Botão "Finalizar / Peticionar" do terminal: roda salvar.sh no VPS (devolve o
  // rascunho editado ao Drive + renderiza a petição .docx + status='pronto'). Síncrono.
  finalizarRefino: (id: number) =>
    _fetch<PecasStatus>(`/api/prazos/${id}/finalizar-refino`, {
      method: 'POST',
      signal: AbortSignal.timeout(190_000),  // espera o salvar.sh terminar
    }),
};

// Ping leve pra status (timeout curto)
export async function ping(): Promise<boolean> {
  try {
    const r = await fetch(`${apiBase()}/healthz`, {
      signal: AbortSignal.timeout(3000),
    });
    return r.ok;
  } catch {
    return false;
  }
}
